import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import { IDEA_CATEGORIES_LIST } from './IdeasNew';
import { ArrowLeft, Lightbulb, ThumbsUp, ThumbsDown, MessageCircle, Eye, Calendar, User, Send, Clock } from 'lucide-react';

// YouTube-style view count: 1000 → 1K, 1500000 → 1.5M
function formatViewCount(n) {
  const num = Number(n) || 0;
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(num);
}

function IdeasDetail({ onNavigate }) {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (id) {
      loadIdea();
    }
  }, [id]);

  const loadIdea = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ideas/ideas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        if (res.status === 404) {
          alert(t('ideas.idea_not_found') || 'Idée non trouvée');
          navigate('/ideas');
          return;
        }
        throw new Error(t('ideas.error_loading_idea') || 'Erreur lors du chargement');
      }

      const data = await res.json();
      setIdea(data.idea);
    } catch (error) {
      console.error('Erreur chargement idée:', error);
      alert(t('ideas.error_loading_idea') || 'Erreur lors du chargement');
      navigate('/ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (voteType) => {
    if (voting || !idea) return;
    
    try {
      setVoting(true);
      const token = localStorage.getItem('token');
      const apiVoteType = voteType === 'upvote' ? 'up' : 'down';
      const res = await fetch(`${API_BASE}/api/ideas/ideas/${id}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ vote_type: apiVoteType })
      });

      if (res.ok) {
        // Recharger l'idée pour obtenir les nouveaux votes
        await loadIdea();
      } else {
        const errorData = await res.json();
        alert(errorData.error || t('ideas.error_voting') || 'Erreur lors du vote');
      }
    } catch (error) {
      console.error('Erreur vote:', error);
      alert(t('ideas.error_voting') || 'Erreur lors du vote');
    } finally {
      setVoting(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || submittingComment) return;

    try {
      setSubmittingComment(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ideas/ideas/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ comment: commentText.trim() })
      });

      if (res.ok) {
        setCommentText('');
        // Recharger l'idée pour obtenir les nouveaux commentaires
        await loadIdea();
      } else {
        const errorData = await res.json();
        alert(errorData.error || t('ideas.error_commenting') || 'Erreur lors de l\'ajout du commentaire');
      }
    } catch (error) {
      console.error('Erreur commentaire:', error);
      alert(t('ideas.error_commenting') || 'Erreur lors de l\'ajout du commentaire');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'rgba(59, 130, 246, 0.2)',
      'under_review': 'rgba(251, 191, 36, 0.2)',
      'approved': 'rgba(16, 185, 129, 0.2)',
      'in_progress': 'rgba(139, 92, 246, 0.2)',
      'implemented': 'rgba(16, 185, 129, 0.2)',
      'rejected': 'rgba(239, 68, 68, 0.2)',
      'archived': 'rgba(107, 114, 128, 0.2)'
    };
    return colors[status] || 'rgba(255, 255, 255, 0.1)';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'submitted': t('ideas.status_submitted'),
      'under_review': t('ideas.status_under_review'),
      'approved': t('ideas.status_approved'),
      'in_progress': t('ideas.status_in_progress'),
      'implemented': t('ideas.status_implemented'),
      'rejected': t('ideas.status_rejected'),
      'archived': t('ideas.status_archived')
    };
    return statusMap[status] || status;
  };

  const getCategoryDisplayName = (categoryName) => {
    if (!categoryName) return '';
    const opt = IDEA_CATEGORIES_LIST.find(c => c.value === categoryName);
    return opt ? t(opt.labelKey) : categoryName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #3b82f6',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('common.loading') || 'Chargement...'}</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!idea) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent comme HomePage */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          <button
            onClick={() => navigate('/ideas')}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              padding: '10px',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flexShrink: 0,
              width: '44px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Lightbulb size={32} color="#fbbf24" />
            {t('ideas.idea_details') || 'Détails de l\'idée'}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div style={{
        paddingTop: 'clamp(100px, 12vw, 140px)',
        paddingBottom: '40px',
        paddingLeft: 'clamp(16px, 4vw, 40px)',
        paddingRight: 'clamp(16px, 4vw, 40px)',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Carte principale */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          {/* En-tête avec statut */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: 'clamp(24px, 4vw, 32px)',
                fontWeight: 700,
                margin: '0 0 15px 0',
                lineHeight: 1.3
              }}>
                {idea.title}
              </h1>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center'
              }}>
                {idea.category_name && (
                  <span style={{
                    background: idea.category_color ? `${idea.category_color}20` : 'rgba(59, 130, 246, 0.2)',
                    color: idea.category_color || '#3b82f6',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    {getCategoryDisplayName(idea.category_name)}
                  </span>
                )}
                <span style={{
                  background: getStatusColor(idea.status),
                  color: '#ffffff',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600
                }}>
                  {getStatusText(idea.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div style={{
            marginBottom: '25px',
            lineHeight: 1.6,
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            whiteSpace: 'pre-wrap'
          }}>
            {idea.description}
          </div>

          {/* Métadonnées */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            {idea.first_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} />
                <span>{idea.first_name} {idea.last_name}</span>
                {idea.department && <span>• {idea.department}</span>}
              </div>
            )}
            {idea.is_anonymous && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} />
                <span>{t('ideas.anonymous') || 'Anonyme'}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={16} />
              <span>{formatDate(idea.created_at)}</span>
            </div>
            {idea.views_count !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title={(idea.views_count ?? 0) + ' ' + (t('ideas.views') || 'views')}>
                <Eye size={16} />
                <span>{formatViewCount(idea.views_count)} {t('ideas.views') || 'views'}</span>
              </div>
            )}
          </div>

          {/* Actions de vote */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '25px',
            paddingTop: '25px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <button
              onClick={() => handleVote('upvote')}
              disabled={voting}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: (idea.user_vote === 'up' || idea.user_vote === 'upvote') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: `1px solid ${(idea.user_vote === 'up' || idea.user_vote === 'upvote') ? '#10b981' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: voting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: voting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!voting) {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                  e.currentTarget.style.borderColor = '#10b981';
                }
              }}
              onMouseLeave={(e) => {
                if (!voting) {
                  e.currentTarget.style.background = (idea.user_vote === 'up' || idea.user_vote === 'upvote') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = (idea.user_vote === 'up' || idea.user_vote === 'upvote') ? '#10b981' : 'rgba(255, 255, 255, 0.2)';
                }
              }}
            >
              <ThumbsUp size={18} />
              {idea.upvotes ?? 0}
            </button>
            <button
              onClick={() => handleVote('downvote')}
              disabled={voting}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: (idea.user_vote === 'down' || idea.user_vote === 'downvote') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: `1px solid ${(idea.user_vote === 'down' || idea.user_vote === 'downvote') ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: voting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: voting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!voting) {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = '#ef4444';
                }
              }}
              onMouseLeave={(e) => {
                if (!voting) {
                  e.currentTarget.style.background = (idea.user_vote === 'down' || idea.user_vote === 'downvote') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = (idea.user_vote === 'down' || idea.user_vote === 'downvote') ? '#ef4444' : 'rgba(255, 255, 255, 0.2)';
                }
              }}
            >
              <ThumbsDown size={18} />
              {idea.downvotes ?? 0}
            </button>
            {(idea.score !== undefined && idea.score !== null) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff'
              }}>
                {t('ideas.score') || 'Score'}: {idea.score ?? 0}
              </div>
            )}
          </div>
        </div>

        {/* Section commentaires */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <MessageCircle size={24} />
            {t('ideas.comments') || 'Commentaires'} ({idea.comments?.length || 0})
          </h2>

          {/* Formulaire de commentaire */}
          <form onSubmit={handleSubmitComment} style={{ marginBottom: '30px' }}>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('ideas.comment_placeholder') || 'Ajouter un commentaire...'}
              rows="4"
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'inherit',
                outline: 'none',
                resize: 'vertical',
                marginBottom: '15px'
              }}
            />
            <button
              type="submit"
              disabled={!commentText.trim() || submittingComment}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: commentText.trim() && !submittingComment ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: commentText.trim() && !submittingComment ? 'pointer' : 'not-allowed',
                opacity: commentText.trim() && !submittingComment ? 1 : 0.6,
                transition: 'all 0.2s'
              }}
            >
              <Send size={18} />
              {submittingComment ? (t('common.loading') || 'Envoi...') : (t('ideas.post_comment') || 'Publier')}
            </button>
          </form>

          {/* Liste des commentaires */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {idea.comments && idea.comments.length > 0 ? (
              idea.comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <User size={18} color="rgba(255, 255, 255, 0.7)" />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '15px' }}>
                          {comment.first_name} {comment.last_name}
                        </div>
                        {comment.department && (
                          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {comment.department}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      <Clock size={14} />
                      {formatDate(comment.created_at)}
                    </div>
                  </div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {comment.comment}
                  </div>
                </div>
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '16px'
              }}>
                {t('ideas.no_comments') || 'Aucun commentaire pour le moment. Soyez le premier à commenter !'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IdeasDetail;

